/**
 * Utilitaires de chiffrement/déchiffrement pour l'API
 * Utilisé pour décoder les réponses cryptées de l'API Django
 */

// Interface pour les données cryptées reçues de l'API
interface EncryptedData {
    data: {
        data: string
        key: string
        nonce: string
        tag: string
    }
}

// Fonction pour vérifier si les données sont cryptées
export function isEncryptedData(data: any): data is EncryptedData {
    return (
        data &&
        typeof data === 'object' &&
        data.data &&
        typeof data.data === 'object' &&
        typeof data.data.data === 'string' &&
        typeof data.data.key === 'string' &&
        typeof data.data.nonce === 'string' &&
        typeof data.data.tag === 'string'
    )
}

/**
 * Déchiffre les données AES-GCM cryptées
 * @param encryptedData - Les données cryptées de l'API
 * @param privateKey - La clé privée RSA au format PEM
 * @returns Les données déchiffrées
 */
export async function decryptApiData(encryptedData: EncryptedData, privateKey: string): Promise<any> {
    try {
        // Extraire les données cryptées du bon niveau
        const cryptoData = encryptedData.data

        // Convertir les chaînes base64 en ArrayBuffer
        const encryptedContent = base64ToArrayBuffer(cryptoData.data)
        const encryptedAesKey = base64ToArrayBuffer(cryptoData.key)
        const nonce = base64ToArrayBuffer(cryptoData.nonce)
        const tag = base64ToArrayBuffer(cryptoData.tag)

        // Tenter de déchiffrer la clé AES avec RSA (SHA-1 puis SHA-256)
        let aesKeyBuffer: ArrayBuffer
        try {
            // D'abord essayer avec SHA-1 (comme dans le serveur)
            const rsaPrivateKeySha1 = await importRSAPrivateKey(privateKey, 'SHA-1')
            aesKeyBuffer = await window.crypto.subtle.decrypt(
                'RSA-OAEP',
                rsaPrivateKeySha1,
                encryptedAesKey
            )
        } catch {
            try {
                // Si SHA-1 échoue, essayer SHA-256
                const rsaPrivateKeySha256 = await importRSAPrivateKey(privateKey, 'SHA-256')
                aesKeyBuffer = await window.crypto.subtle.decrypt(
                    'RSA-OAEP',
                    rsaPrivateKeySha256,
                    encryptedAesKey
                )
            } catch (error: any) {
                // throw new Error(`Échec du déchiffrement RSA-OAEP avec SHA-1 et SHA-256: ${error?.message || error}`)
            }
        }

        // Importer la clé AES
        const aesKey = await window.crypto.subtle.importKey(
            'raw',
            aesKeyBuffer,
            { name: 'AES-GCM' },
            false,
            ['decrypt']
        )

        // Combiner les données cryptées avec le tag pour AES-GCM
        const encryptedWithTag = new Uint8Array(encryptedContent.byteLength + tag.byteLength)
        encryptedWithTag.set(new Uint8Array(encryptedContent), 0)
        encryptedWithTag.set(new Uint8Array(tag), encryptedContent.byteLength)

        // Déchiffrer les données avec AES-GCM
        const decryptedBuffer = await window.crypto.subtle.decrypt(
            {
                name: 'AES-GCM',
                iv: nonce,
                tagLength: 128
            },
            aesKey,
            encryptedWithTag
        )

        // Convertir le buffer en string et parser en JSON
        const decryptedText = new TextDecoder().decode(decryptedBuffer)
        return JSON.parse(decryptedText)

    } catch (error: any) {
        // console.error('Erreur lors du déchiffrement:', error)
        // throw new Error(`Échec du déchiffrement: ${error?.message || error}`)
    }
}

/**
 * Convertit une chaîne base64 en ArrayBuffer
 */
function base64ToArrayBuffer(base64: string): ArrayBuffer {
    const binaryString = atob(base64)
    const bytes = new Uint8Array(binaryString.length)
    for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i)
    }
    return bytes.buffer
}

/**
 * Importe une clé privée RSA au format PEM
 */
async function importRSAPrivateKey(pemKey: string, hashAlgo: 'SHA-1' | 'SHA-256' = 'SHA-256'): Promise<CryptoKey> {
    // Nettoyer la clé PEM (enlever les headers et les retours à la ligne)
    const pemHeader = '-----BEGIN PRIVATE KEY-----'
    const pemFooter = '-----END PRIVATE KEY-----'
    const pemContents = pemKey
        .replace(pemHeader, '')
        .replace(pemFooter, '')
        .replace(/\s/g, '')

    // Convertir en ArrayBuffer
    const binaryDer = base64ToArrayBuffer(pemContents)

    // Importer la clé
    return await window.crypto.subtle.importKey(
        'pkcs8',
        binaryDer,
        {
            name: 'RSA-OAEP',
            hash: hashAlgo
        },
        false,
        ['decrypt']
    )
}

/**
 * Charge la clé privée depuis la configuration runtime Nuxt
 */
export async function loadPrivateKeyFromServer(): Promise<string | null> {
    try {

        // Charger depuis la configuration runtime Nuxt
        const config = useRuntimeConfig()

        // Utiliser privateKeyPem depuis la configuration
        const privateKey = config.privateKeyPem

        if (privateKey && privateKey.trim().length > 0) {
            return privateKey.trim()
        }

        return null

    } catch (error) {
        return null
    }
}

/**
 * Gère le stockage sécurisé de la clé privée
 */
export class PrivateKeyManager {
    private static readonly STORAGE_KEY = 'mydit_private_key'
    private static instance: PrivateKeyManager
    private privateKey: string | null = null
    private isLoading: boolean = false
    private loadPromise: Promise<void> | null = null

    private constructor() {
        this.loadPrivateKey()
    }

    static getInstance(): PrivateKeyManager {
        if (!PrivateKeyManager.instance) {
            PrivateKeyManager.instance = new PrivateKeyManager()
        }
        return PrivateKeyManager.instance
    }

    /**
     * Stocke la clé privée (en mémoire pour cette session)
     */
    setPrivateKey(key: string): void {
        this.privateKey = key
        // Pour plus de sécurité, on pourrait aussi la stocker dans sessionStorage
        if (typeof window !== 'undefined') {
            sessionStorage.setItem(PrivateKeyManager.STORAGE_KEY, key)
        }
    }

    /**
     * Récupère la clé privée
     */
    getPrivateKey(): string | null {
        return this.privateKey
    }

    /**
     * Charge la clé privée depuis le stockage ou depuis le serveur
     */
    private async loadPrivateKey(): Promise<void> {
        if (typeof window !== 'undefined') {
            // D'abord essayer de charger depuis sessionStorage
            const storedKey = sessionStorage.getItem(PrivateKeyManager.STORAGE_KEY)
            if (storedKey) {
                this.privateKey = storedKey
                return
            }
        }

        // Sinon, charger depuis le serveur
        await this.loadFromServer()
    }

    /**
     * Charge la clé privée depuis le serveur
     */
    async loadFromServer(): Promise<void> {
        if (this.isLoading && this.loadPromise) {
            return this.loadPromise
        }

        this.isLoading = true
        this.loadPromise = (async () => {
            try {
                const privateKey = await loadPrivateKeyFromServer()
                if (privateKey) {
                    this.setPrivateKey(privateKey)
                }
            } catch (error) {
                // console.error('Erreur lors du chargement de la clé privée depuis le serveur:', error)
            } finally {
                this.isLoading = false
                this.loadPromise = null
            }
        })()

        return this.loadPromise
    }

    /**
     * Assure que la clé privée est chargée
     */
    async ensurePrivateKey(): Promise<string | null> {
        if (this.privateKey) {
            return this.privateKey
        }

        if (this.isLoading && this.loadPromise) {
            await this.loadPromise
            return this.privateKey
        }

        await this.loadFromServer()
        return this.privateKey
    }

    /**
     * Supprime la clé privée
     */
    clearPrivateKey(): void {
        this.privateKey = null
        if (typeof window !== 'undefined') {
            sessionStorage.removeItem(PrivateKeyManager.STORAGE_KEY)
        }
    }

    /**
     * Vérifie si une clé privée est disponible
     */
    hasPrivateKey(): boolean {
        return this.privateKey !== null && this.privateKey.length > 0
    }

    /**
     * Vérifie de manière asynchrone si une clé privée peut être chargée
     */
    async hasPrivateKeyAsync(): Promise<boolean> {
        const key = await this.ensurePrivateKey()
        return key !== null && key.length > 0
    }
}
